import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";

export type PromoApplied = { code: string; amount_off: number; new_total: number };

export function PromoInput({
  context, subtotal, value, onChange, onApplied, applied,
}: {
  context: "booking" | "shop";
  subtotal: number;
  value: string;
  onChange: (v: string) => void;
  applied: PromoApplied | null;
  onApplied: (p: PromoApplied | null) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function apply() {
    if (!value.trim()) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("validate-promo", {
      body: { code: value.trim(), context, subtotal },
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    if (!data?.ok) return toast.error(data?.error || "Invalid promo");
    onApplied({ code: data.code, amount_off: data.amount_off, new_total: data.new_total });
    toast.success(`Promo applied: −$${Number(data.amount_off).toFixed(2)}`);
  }

  if (applied) {
    return (
      <div className="flex items-center justify-between gap-2 text-xs bg-primary/10 border border-primary/40 px-3 py-2 mt-2">
        <span className="font-mono">PROMO: <strong>{applied.code}</strong></span>
        <button onClick={() => { onApplied(null); onChange(""); }} className="text-muted-foreground hover:text-foreground">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex gap-2 mt-2">
      <Input placeholder="Promo code" value={value} onChange={e => onChange(e.target.value)} className="text-xs" />
      <Button type="button" size="sm" variant="outline" onClick={apply} disabled={busy || !value.trim()}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
      </Button>
    </div>
  );
}